import { Button, Table, Input, Modal, Spin, Empty, Space, Popconfirm, Upload, Tabs } from "antd";
import axios from "axios";
import { useState, useEffect } from "react";

import { extractEmailsFromCSV, extractEmailsFromExcel } from "../../../../utils/file-reader-utils";
import { showSuccessNotification, showWarningNotification } from "../../../../utils/notification";

const DiscountAllowlistModal = ({
  ROOT_URL,
  BASE_URL,
  discountParams = { discountId: "", discountName: "" },
  isModalOpen = false,
  setIsModalOpen = () => {}
}) => {
  const [form, setForm] = useState({ email: "" });
  const [allEmails, setAllEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationParams, setPaginationParams] = useState({ current: 1, pageSize: 10 });
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [uploadedEmails, setUploadedEmails] = useState([]);
  const [invalidEmails, setInvalidEmails] = useState([]); // State for invalid emails
  const [isFileLoading, setIsFileLoading] = useState(false);
  const FILE_FORMAT = `
"id","email"
"1","user1@example.com"
"2","user2@example.com"`;

  useEffect(() => {
    if (isModalOpen && discountParams) {
      fetchAllowlistEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, discountParams]);

  const fetchAllowlistEmails = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${ROOT_URL}/${BASE_URL}/allowlist/${discountParams.discountId}`
      );

      const { allowlistEmailIds = [] } = response?.data || {};
      setAllEmails(allowlistEmailIds);
      setPaginationParams({ current: 1, pageSize: 10 });
    } catch (err) {
      if (err.response?.status === 404) {
        setAllEmails([]);
      }
      showWarningNotification(err.response?.data || "Failed to fetch allowlist emails");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSingleEmail = async () => {
    const inputEmail = form.email?.trim();
    if (!inputEmail) {
      showWarningNotification("Please enter an email address");
      return;
    }

    setIsAddingEmail(true);
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/allowlist/add-emails`, {
        discountId: discountParams.discountId,
        emails: [inputEmail]
      });
      showSuccessNotification("Email added successfully");
      setForm({ email: "" });
      setIsAddModalVisible(false);
      fetchAllowlistEmails();
    } catch (err) {
      showWarningNotification(err.response?.data || "Failed to add email");
      console.error(err);
    } finally {
      setIsAddingEmail(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/allowlist/remove-email`, {
        discountId: discountParams.discountId,
        email
      });

      showSuccessNotification("Email removed successfully");
      fetchAllowlistEmails();
    } catch (err) {
      showWarningNotification(err.response?.data || "Failed to remove email");
      console.error(err);
    }
  };

  const extractEmailsFromFile = async (file) => {
    setIsFileLoading(true);
    try {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      let results = { validEmails: [], invalidEmails: [] }; // Expect object now
      if (fileExtension === "csv") {
        results = await extractEmailsFromCSV(file);
      } else if (fileExtension === "xlsx" || fileExtension === "xls") {
        results = await extractEmailsFromExcel(file);
      }
      setUploadedEmails(results.validEmails); // Set valid emails
      setInvalidEmails(results.invalidEmails); // Set invalid emails
      setIsFileLoading(false);
      return results.validEmails;
    } catch (err) {
      showWarningNotification(`Error processing file: ${err.message}`);
      setIsFileLoading(false);
      setInvalidEmails([]);
      return [];
    }
  };

  const handleUploadEmails = async () => {
    if (uploadedEmails.length === 0) {
      showWarningNotification("No valid emails found in the file");
      return;
    }

    setIsAddingEmail(true);
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/allowlist/add-emails`, {
        discountId: discountParams.discountId,
        emails: uploadedEmails
      });

      showSuccessNotification(`${uploadedEmails.length} emails added successfully`);
      setUploadedEmails([]);
      setInvalidEmails([]);
      setIsAddModalVisible(false);
      fetchAllowlistEmails();
    } catch (err) {
      showWarningNotification(err.response?.data || "Failed to add emails");
      console.error(err);
    } finally {
      setIsAddingEmail(false);
    }
  };

  // Paginate data on the frontend
  const startIndex = (paginationParams.current - 1) * paginationParams.pageSize;
  const endIndex = startIndex + paginationParams.pageSize;
  const paginatedEmails = allEmails.slice(startIndex, endIndex).map((email, idx) => ({
    key: email,
    email,
    index: startIndex + idx + 1
  }));

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 50
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Remove Email"
          description="Are you sure you want to remove this email?"
          onConfirm={() => handleRemoveEmail(record.email)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" danger size="small">
            Remove
          </Button>
        </Popconfirm>
      )
    }
  ];

  const tabItems = [
    {
      key: "manual",
      label: "Manual Entry",
      children: (
        <div>
          <Input
            placeholder="Enter email address"
            value={form.email}
            onChange={(e) => setForm({ email: e.target.value })}
            onPressEnter={handleAddSingleEmail}
            type="email"
            style={{ marginBottom: 16 }}
          />
          <Button type="primary" onClick={handleAddSingleEmail} loading={isAddingEmail} block>
            Add Email
          </Button>
        </div>
      )
    },
    {
      key: "bulk",
      label: "Bulk Upload",
      children: (
        <Spin spinning={isFileLoading}>
          <div>
            <div
              style={{ marginBottom: 12, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}
            >
              <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                <strong>Expected file format:</strong>
              </p>
              <pre style={{ fontSize: 11, color: "#333", textAlign: "left" }}>{FILE_FORMAT}</pre>
            </div>
            <Upload
              accept=".csv,.xlsx,.xls"
              maxCount={1}
              beforeUpload={(file) => {
                extractEmailsFromFile(file);
                return false;
              }}
              onRemove={() => setUploadedEmails([])}
            >
              <Button>Select CSV or Excel File</Button>
            </Upload>

            {(uploadedEmails.length > 0 || invalidEmails.length > 0) && ( // Show results section if either list is populated
              <div style={{ marginTop: 16 }}>
                {/* VALID EMAILS DISPLAY */}
                {uploadedEmails.length > 0 && (
                  <>
                    <p>
                      <strong>Found {uploadedEmails.length} valid email(s):</strong>
                    </p>
                    <div
                      style={{
                        maxHeight: 150,
                        overflowY: "auto",
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        padding: 8,
                        marginBottom: invalidEmails.length > 0 ? "8px" : "16px" // Adjust spacing if invalid list follows
                      }}
                    >
                      {uploadedEmails.map((email, idx) => (
                        <div key={idx} style={{ fontSize: 12, marginBottom: 4 }}>
                          {email}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="primary"
                      onClick={handleUploadEmails}
                      loading={isAddingEmail}
                      block
                      style={{ marginBottom: invalidEmails.length > 0 ? "16px" : "0" }}
                    >
                      Add All Emails
                    </Button>
                  </>
                )}

                {/* INVALID EMAILS DISPLAY */}
                {invalidEmails.length > 0 && (
                  <>
                    <p
                      style={{
                        color: "#cf1322",
                        marginTop: uploadedEmails.length === 0 ? "0" : "16px"
                      }}
                    >
                      <strong>Found {invalidEmails.length} invalid entry(ies) (Not added):</strong>
                    </p>
                    <div
                      style={{
                        maxHeight: 150,
                        overflowY: "auto",
                        border: "1px solid #ffccc7",
                        backgroundColor: "#fff1f0",
                        borderRadius: 4,
                        padding: 8,
                        marginBottom: 0
                      }}
                    >
                      {invalidEmails.map((email, idx) => (
                        <div key={idx} style={{ fontSize: 12, marginBottom: 4, color: "#cf1322" }}>
                          <span role="img" aria-label="warning">
                            &#9888;
                          </span>{" "}
                          {email}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Spin>
      )
    }
  ];

  return (
    <Modal
      title={`Manage Allowlist - ${discountParams.discountName}`}
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      width={700}
      footer={[
        <Button key="close" onClick={() => setIsModalOpen(false)}>
          Close
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" onClick={() => setIsAddModalVisible(true)}>
            Add Email
          </Button>
          <Button onClick={() => fetchAllowlistEmails()} loading={isLoading}>
            Refresh
          </Button>
        </Space>
      </div>

      <Spin spinning={isLoading}>
        <Table
          columns={columns}
          dataSource={paginatedEmails}
          scroll={{ x: "max-content" }}
          pagination={{
            current: paginationParams.current,
            pageSize: paginationParams.pageSize,
            total: allEmails.length,
            onChange: (page) => setPaginationParams({ ...paginationParams, current: page }),
            onShowSizeChange: (_, pageSize) => setPaginationParams({ current: 1, pageSize }),
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"]
          }}
          locale={{
            emptyText: <Empty description="No emails in allowlist" />
          }}
        />
      </Spin>

      <Modal
        title="Add Emails to Allowlist"
        open={isAddModalVisible}
        onCancel={() => {
          setIsAddModalVisible(false);
          setForm({ email: "" });
          setUploadedEmails([]);
        }}
        footer={null}
        width={500}
      >
        <Tabs items={tabItems} />
      </Modal>
    </Modal>
  );
};

export default DiscountAllowlistModal;
